import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// `withCredentials: true` → el navegador envía la cookie HttpOnly `aea_refresh`
// junto con las peticiones a /api/auth/refresh y /api/auth/logout. El backend
// exige CORS_ALLOW_CREDENTIALS=true y una lista blanca de orígenes (nunca "*").
const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
  withCredentials: true,
});

let refreshInFlight = null;

async function tryRefresh() {
  if (refreshInFlight) return refreshInFlight;
  // El refresh_token viaja en cookie HttpOnly; el navegador la adjunta si
  // withCredentials=true. NUNCA la leemos desde JavaScript.
  refreshInFlight = axios.post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true })
    .then((r) => {
      const at = r.data.access_token || r.data.token;
      if (at) localStorage.setItem('aea_token', at);
      return at;
    })
    .catch(() => {
      localStorage.removeItem('aea_token');
      localStorage.removeItem('aea_user');
      return null;
    })
    .finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('aea_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
}, (error) => Promise.reject(error));

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    if (error.response?.status === 401 &&
        !original._retry &&
        !original.url?.includes('/auth/login') &&
        !original.url?.includes('/auth/register') &&
        !original.url?.includes('/auth/refresh')) {
      original._retry = true;
      const newToken = await tryRefresh();
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      localStorage.removeItem('aea_token');
      localStorage.removeItem('aea_user');
      if (typeof window !== 'undefined') window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  refresh: () => api.post('/auth/refresh', {}),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
  mySessions: () => api.get('/auth/sessions'),
  revokeSession: (id) => api.delete(`/auth/sessions/${id}`),
};

export const membersApi = {
  list: () => api.get('/members'),
  validate: (id) => api.put(`/members/${id}/validate`),
  update: (id, data) => api.put(`/members/${id}`, data),
  resetPassword: (id, newPassword) => api.post(`/members/${id}/reset-password`, { new_password: newPassword }),
  resetData: (id) => api.delete(`/members/${id}/data`),
  remove: (id) => api.delete(`/members/${id}`),
  sessions: (id) => api.get(`/members/${id}/sessions`),
};

export const contractsApi = {
  getMy: () => api.get('/contracts/my'),
  sign: () => api.post('/contracts/sign'),
  downloadPdf: (id) => api.get(`/contracts/${id}/pdf`, { responseType: 'blob' }),
};

export const indicatorsApi = {
  list: () => api.get('/indicators'),
  create: (data) => api.post('/indicators', data),
  remove: (code) => api.delete(`/indicators/${code}`),
};

export const campaignsApi = {
  list: () => api.get('/campaigns'),
  get: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post('/campaigns', data),
  close: (id) => api.put(`/campaigns/${id}/close`),
  reopen: (id) => api.put(`/campaigns/${id}/reopen`),
  remove: (id) => api.delete(`/campaigns/${id}`),
  downloadTemplate: (id) => api.get(`/campaigns/${id}/template`, { responseType: 'blob' }),
};

export const submissionsApi = {
  submit: (campaignId, formData) => api.post(`/submissions/${campaignId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  mine: () => api.get('/submissions/mine'),
  all: (campaignId) => api.get('/submissions', { params: campaignId ? { campaign_id: campaignId } : {} }),
};

export const statsApi = {
  company: (companyId) => api.get(`/stats/company/${companyId}`),
  benchmark: (companyId, year, month) => api.get(`/stats/benchmark/${companyId}`, {
    params: (year && month) ? { year, month } : {},
  }),
  sector: (year, month) => api.get('/stats/sector', {
    params: (year && month) ? { year, month } : {},
  }),
  evolution: (indicatorCode, companyId) => api.get(`/stats/evolution/${indicatorCode}`, {
    params: companyId ? { company_id: companyId } : {},
  }),
};

export const dashboardApi = {
  admin: () => api.get('/admin/dashboard'),
  company: () => api.get('/company/dashboard'),
};

export const auditApi = {
  list: (params) => api.get('/audit', { params }),
  export: () => api.get('/audit/export', { responseType: 'blob' }),
};

export const incidentsApi = {
  list: () => api.get('/incidents'),
  create: (data) => api.post('/incidents', data),
  update: (id, data) => {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => v !== undefined && fd.append(k, v));
    return api.put(`/incidents/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const aiApi = {
  chat: (sessionId, message) => api.post('/ai/chat', { session_id: sessionId, message }),
  history: (sessionId) => api.get(`/ai/history/${sessionId}`),
};

export const docsApi = {
  downloadManual: (version) => api.get('/docs/manual', {
    params: version ? { version } : {},
    responseType: 'blob',
  }),
  getManualMarkdown: (version) => api.get('/docs/manual/markdown', {
    params: version ? { version } : {},
  }),
  listVersions: () => api.get('/docs/manual/versions'),
  createVersion: (data) => api.post('/docs/manual/versions', data),
  deleteVersion: (version) => api.delete(`/docs/manual/versions/${version}`),
};

export const catalogApi = {
  list: () => api.get('/catalog/products'),
  get: (id) => api.get(`/catalog/products/${id}`),
  dcat: (id) => api.get(`/catalog/products/${id}/dcat`),
  create: (data) => api.post('/catalog/products', data),
  update: (id, data) => api.put(`/catalog/products/${id}`, data),
  publish: (id) => api.post(`/catalog/products/${id}/publish`),
  withdraw: (id) => api.post(`/catalog/products/${id}/withdraw`),
  myPublishableSubmissions: () => api.get('/catalog/my-publishable-submissions'),
  createPublication: (productId, data) =>
    api.post(`/catalog/products/${productId}/publications`, data),
  myPublications: () => api.get('/catalog/publications/mine'),
  requestAccess: (productId, data) =>
    api.post(`/catalog/products/${productId}/access-requests`, data),
  myRequests: () => api.get('/catalog/access-requests/mine'),
  accessTransaction: (reqOrProductId, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return api.post(`/catalog/transactions/${reqOrProductId}/access${q ? '?' + q : ''}`);
  },
  myTransactions: () => api.get('/catalog/transactions/mine'),
  adminPublications: () => api.get('/catalog/admin/publications'),
  adminRequests: () => api.get('/catalog/admin/access-requests'),
  adminTransactions: () => api.get('/catalog/admin/transactions'),
};

export default api;
